/**
 * Estado da anonimização no painel (WEL-5343 / WEL-5344).
 *
 * A mudança de fundo: o navegador DEIXA de ser a fonte da verdade. Antes o
 * `presentationMode` no localStorage decidia se o dado vinha coberto, e o
 * próprio usuário o editava pelo DevTools. Agora quem decide é o servidor, e o
 * padrão dele é mascarar.
 *
 * Sobrou aqui um estado local só, e ele é seguro por construção: a máscara
 * FORÇADA. Ela só consegue cobrir mais, nunca menos, então continuar no
 * localStorage não abre brecha nenhuma. É o botão de Modo Apresentação, para
 * quem liberou os dados e quer voltar a cobrir a tela sem esperar as 48h.
 */
const state = {
  mascaraForcada: localStorage.getItem("presentationMode") === "true" || false
};

const mutations = {
  TOGGLE_PRESENTATION_MODE(state) {
    state.mascaraForcada = !state.mascaraForcada;
    localStorage.setItem("presentationMode", state.mascaraForcada);
  },
  SET_PRESENTATION_MODE(state, value) {
    state.mascaraForcada = value;
    localStorage.setItem("presentationMode", state.mascaraForcada);
  }
};

const actions = {
  togglePresentationMode({ commit }) {
    commit("TOGGLE_PRESENTATION_MODE");
  },
  setPresentationMode({ commit }, value) {
    commit("SET_PRESENTATION_MODE", value);
  }
};

const getters = {
  /**
   * O que vai no cabeçalho X-Presentation-Mode.
   *
   * Só a máscara forçada, deliberadamente. O servidor já mascara sozinho quando
   * o usuário não está liberado, e mandar `true` sempre faria o modo relatório e
   * o desligado aplicarem a máscara legada de asteriscos em toda tela, para todo
   * mundo, sem ninguém ter pedido.
   */
  isPresentationMode: (state) => state.mascaraForcada,

  /** O servidor está com o motor novo ligado? Espelho do verificar-login. */
  anonimizacaoAtiva: (state, getters) =>
    getters.currentUserPersonalInfo?.anonimizacao_ativa || false,

  /** A liberação por segundo fator está valendo? Espelho do verificar-login. */
  anonimizacaoLiberada: (state, getters) =>
    getters.currentUserPersonalInfo?.anonimizacao_liberada || false,

  anonimizacaoLiberadaAte: (state, getters) =>
    getters.currentUserPersonalInfo?.anonimizacao_liberada_ate || null,

  /**
   * O servidor decide por quanto tempo o botao se apresenta, porque a conta e
   * feita com a data de cadastro do usuario, que a tela nao tem, e porque assim
   * desligar a apresentacao e mudar uma variavel de ambiente.
   */
  anonimizacaoApresentar: (state, getters) =>
    getters.currentUserPersonalInfo?.anonimizacao_apresentar || false,

  /**
   * O dado que está na tela agora vem coberto?
   *
   * É isto que a interface deve ler para decidir o que exibir, e não o
   * `isPresentationMode`: com o motor ligado, quem não liberou está mascarado
   * mesmo sem nunca ter tocado no botão.
   */
  dadosMascarados: (state, getters) => {
    if (!getters.anonimizacaoAtiva) {
      return state.mascaraForcada;
    }

    return state.mascaraForcada || !getters.anonimizacaoLiberada;
  },

  /**
   * O navegador precisa cobrir por conta própria?
   *
   * Só quando o motor está desligado. Com ele ligado o servidor já devolve o
   * dado coberto, e borrar por cima é cobertura dupla: a tela fica ilegível sem
   * proteger nada a mais, porque o valor real nem está na resposta. É a queixa
   * que originou o projeto inteiro.
   *
   * Existe como getter, e não repetida em cada lugar que a consulta, porque a
   * mesma regra escrita em dois pontos foi a origem de quase todo o
   * comportamento errático desta entrega. Regra em um lugar só não diverge de
   * si mesma.
   */
  cobrirNoNavegador: (state, getters) =>
    getters.dadosMascarados && !getters.anonimizacaoAtiva
};

export default {
  state,
  mutations,
  actions,
  getters
};
